// =============================================
// MusicUpload — add tracks to the library (single / bulk + ownership consent)
// =============================================
// Pick audio (or video, if the plan allows) files → a review queue with editable
// title/artist/album → certify ownership (consent modal) → commit. Each commit
// records a per-upload ownership consent (mus_legal_acceptance) and stamps its batch
// id onto every track, stores the blob in IndexedDB, and writes the catalog rows.
//
// Single-file upload is available to everyone; BULK upload is a paid feature
// (free/ad-supported users are limited to one file at a time).
// =============================================
import React, { useCallback, useRef, useState } from 'react';
import { FiUploadCloud, FiX, FiLock, FiImage } from 'react-icons/fi';
import { storeAudioBlob } from '../../lib/audioStore';
import { canUploadTrack, getMusicEntitlement } from '../../lib/musicEntitlements';
import { isPlatformActor } from '../../lib/entitlements';
import { recordUploadConsent, UPLOAD_CONSENT } from '../../lib/musicLegal';
import { extractCoverFromFile, isValidCover } from './coverArt';
import { uploadTrack } from './musicLibrary';
import MusicLegalModal from './MusicLegalModal';

const AUDIO_EXTS = ['.mp3', '.m4a', '.aac', '.flac', '.wav', '.ogg', '.oga'];
const VIDEO_EXTS = ['.mp4', '.webm', '.mov'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB per file

type UploadMode = 'single' | 'bulk';

interface QueuedTrack {
  id: string;
  file: File;
  title: string;
  artist: string;
  album: string;
  durationSeconds: number;
  isVideo: boolean;
  coverBlob: Blob | null;   // auto-extracted or user-picked
  coverUrl: string | null;  // preview object URL (revoked on remove/clear)
  status: 'queued' | 'saving' | 'done' | 'error';
  errorMsg?: string;
}

interface MusicUploadProps {
  userId: number;
  signatureDefault?: string;
  onUploaded: () => void;
}

const ext = (name: string) => '.' + (name.split('.').pop()?.toLowerCase() || '');
const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const fmtMb = (b: number) => (b / (1024 * 1024)).toFixed(1) + ' MB';

function parseName(filename: string): { title: string; artist: string } {
  const base = filename.replace(/\.[^.]+$/, '').trim();
  const m = base.match(/^(.+?)\s+-\s+(.+)$/);
  if (m) return { artist: m[1].trim(), title: m[2].trim() };
  return { artist: '', title: base };
}

function readDuration(file: File, isVideo: boolean): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(isVideo ? 'video' : 'audio') as HTMLMediaElement;
    const done = (d: number) => { URL.revokeObjectURL(url); resolve(isFinite(d) ? d : 0); };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(el.duration);
    el.onerror = () => done(0);
    el.src = url;
  });
}

export const MusicUpload: React.FC<MusicUploadProps> = ({ userId, signatureDefault, onUploaded }) => {
  const ent = getMusicEntitlement(userId);
  // Bulk upload is a paid feature for regular users, but platform owners/admins
  // (site owner, admins, etc.) always get it — same pattern as Client's Accounts.
  const bulkAllowed = isPlatformActor() || (ent.planCode !== 'FREE' && ent.planCode !== '');

  const [mode, setMode] = useState<UploadMode>('single');
  const [queue, setQueue] = useState<QueuedTrack[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const coverTargetRef = useRef<string | null>(null);
  const dragCountRef = useRef(0);

  const effectiveMode: UploadMode = bulkAllowed ? mode : 'single';
  const accepted = [...AUDIO_EXTS, ...(ent.canUploadVideo ? VIDEO_EXTS : [])];

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    let arr = Array.from(fileList);
    if (effectiveMode === 'single') arr = arr.slice(0, 1); // single mode: one file only
    const added: QueuedTrack[] = [];
    const errBase = { coverBlob: null, coverUrl: null };
    for (const file of arr) {
      const e = ext(file.name);
      const isVideo = VIDEO_EXTS.includes(e);
      const isAudio = AUDIO_EXTS.includes(e);
      if (!isAudio && !isVideo) {
        added.push({ id: genId(), file, title: file.name, artist: '', album: '', durationSeconds: 0, isVideo, ...errBase, status: 'error', errorMsg: `Unsupported format (${e})` });
        continue;
      }
      if (isVideo && !ent.canUploadVideo) {
        added.push({ id: genId(), file, title: file.name, artist: '', album: '', durationSeconds: 0, isVideo, ...errBase, status: 'error', errorMsg: 'Video needs a higher plan' });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        added.push({ id: genId(), file, title: file.name, artist: '', album: '', durationSeconds: 0, isVideo, ...errBase, status: 'error', errorMsg: `Too large (${fmtMb(file.size)}, max ${fmtMb(MAX_FILE_SIZE)})` });
        continue;
      }
      const { title, artist } = parseName(file.name);
      const durationSeconds = await readDuration(file, isVideo);
      // Auto-extract embedded album art (best-effort; null for WAV/no-art files).
      const coverBlob = isVideo ? null : await extractCoverFromFile(file);
      const coverUrl = coverBlob ? URL.createObjectURL(coverBlob) : null;
      added.push({ id: genId(), file, title, artist, album: '', durationSeconds, isVideo, coverBlob, coverUrl, status: 'queued' });
    }
    // Single mode replaces the queue; bulk appends.
    setQueue(prev => {
      if (effectiveMode === 'single') { prev.forEach(p => p.coverUrl && URL.revokeObjectURL(p.coverUrl)); return added; }
      return [...prev, ...added];
    });
  }, [effectiveMode, ent.canUploadVideo]);

  const updateField = (id: string, field: 'title' | 'artist' | 'album', value: string) =>
    setQueue(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
  const removeItem = (id: string) => setQueue(prev => {
    const it = prev.find(q => q.id === id);
    if (it?.coverUrl) URL.revokeObjectURL(it.coverUrl);
    return prev.filter(q => q.id !== id);
  });
  const clearAll = () => setQueue(prev => { prev.forEach(p => p.coverUrl && URL.revokeObjectURL(p.coverUrl)); return []; });

  // ---- Manual cover set/replace ----
  const triggerCover = (id: string) => { coverTargetRef.current = id; coverInputRef.current?.click(); };
  const onCoverPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const id = coverTargetRef.current;
    if (!file || !id) return;
    if (!isValidCover(file)) { window.alert('Pick an image (png/jpg/webp/gif) under 8 MB.'); return; }
    const url = URL.createObjectURL(file);
    setQueue(prev => prev.map(q => {
      if (q.id !== id) return q;
      if (q.coverUrl) URL.revokeObjectURL(q.coverUrl);
      return { ...q, coverBlob: file, coverUrl: url };
    }));
  };

  const onDragEnter = (e: React.DragEvent) => { e.preventDefault(); dragCountRef.current++; setIsDragging(true); };
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); dragCountRef.current--; if (dragCountRef.current <= 0) setIsDragging(false); };
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); dragCountRef.current = 0; setIsDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const queuedCount = queue.filter(q => q.status === 'queued').length;
  const doneCount = queue.filter(q => q.status === 'done').length;

  // ---- Commit: certify ownership (consent), then store each file ----
  const doCommit = useCallback(async (signatureName: string) => {
    const pending = queue.filter(q => q.status === 'queued');
    if (!pending.length) { setShowConsent(false); return; }
    setCommitting(true);
    // Record the per-upload ownership consent first; bind its batch id to the tracks.
    const batchId = await recordUploadConsent(userId, pending.length, signatureName);
    for (const q of pending) {
      const gate = canUploadTrack(q.file.size, q.isVideo, userId);
      if (!gate.allowed) {
        setQueue(prev => prev.map(x => x.id === q.id ? { ...x, status: 'error', errorMsg: gate.reason } : x));
        continue;
      }
      setQueue(prev => prev.map(x => x.id === q.id ? { ...x, status: 'saving' } : x));
      try {
        // Persist bytes to the SERVER as soon as processing finishes (so a cleared
        // browser never loses the file); idb fallback only when offline.
        const key = await storeAudioBlob(q.file, userId, q.file.name);
        let coverKey: string | undefined;
        if (q.coverBlob) { coverKey = await storeAudioBlob(q.coverBlob, userId, 'cover.jpg'); }
        await uploadTrack({
          userId,
          title: q.title.trim() || q.file.name,
          artist: q.artist.trim() || 'Unknown Artist',
          album: q.album.trim(),
          durationSeconds: q.durationSeconds,
          fileSizeBytes: q.file.size,
          fileFormat: ext(q.file.name).slice(1),
          mediaType: q.isVideo ? 'video' : 'audio',
          storageKey: key,
          originalFilename: q.file.name,
          consentBatchId: batchId,
          coverKey,
        });
        setQueue(prev => prev.map(x => x.id === q.id ? { ...x, status: 'done' } : x));
        onUploaded();
      } catch (err: any) {
        setQueue(prev => prev.map(x => x.id === q.id ? { ...x, status: 'error', errorMsg: err?.message || 'Save failed' } : x));
      }
    }
    setCommitting(false);
    setShowConsent(false);
  }, [queue, userId, onUploaded]);

  return (
    <div className="mu-wrap">
      {/* Mode toggle */}
      <div className="mu-mode">
        <button className={`mu-mode-btn ${effectiveMode === 'single' ? 'active' : ''}`} onClick={() => setMode('single')}>
          Single file
        </button>
        <button
          className={`mu-mode-btn ${effectiveMode === 'bulk' ? 'active' : ''} ${!bulkAllowed ? 'locked' : ''}`}
          onClick={() => bulkAllowed && setMode('bulk')}
          disabled={!bulkAllowed}
          title={bulkAllowed ? '' : 'Bulk upload is a paid feature'}
        >
          {!bulkAllowed && <FiLock />} Bulk upload
        </button>
      </div>
      {!bulkAllowed && (
        <p className="mu-bulk-note">Free plan uploads one file at a time. Upgrade in the Membership tab to bulk-upload your whole collection.</p>
      )}

      <div
        className={`mu-dropzone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accepted.join(',')}
          multiple={effectiveMode === 'bulk'}
          style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
        />
        <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onCoverPicked} />
        <FiUploadCloud className="mu-dropzone-icon" />
        <p className="mu-dropzone-title">
          {isDragging ? 'Drop your music here' : (effectiveMode === 'single' ? 'Drag & drop a song, or click to browse' : 'Drag & drop your music, or click to browse')}
        </p>
        <p className="mu-dropzone-meta">
          {AUDIO_EXTS.map(e => e.slice(1)).join(', ')}
          {ent.canUploadVideo ? ` · video: ${VIDEO_EXTS.map(e => e.slice(1)).join(', ')}` : ' · video needs a higher plan'}
          {' '}· max {fmtMb(MAX_FILE_SIZE)} per file
        </p>
      </div>

      {queue.length > 0 && (
        <div className="mu-queue">
          <div className="mu-queue-head">
            <h3>Review &amp; add ({queue.length})</h3>
            <div className="mu-queue-actions">
              <button className="mu-btn mu-btn-secondary" onClick={clearAll} disabled={committing}>Clear</button>
              <button className="mu-btn mu-btn-primary" onClick={() => setShowConsent(true)} disabled={committing || queuedCount === 0}>
                {committing ? 'Adding…' : `Add ${queuedCount || ''} to library`}
              </button>
            </div>
          </div>

          {queue.map(q => (
            <div key={q.id} className={`mu-item mu-item-${q.status}`}>
              {q.status === 'error' ? (
                <div className="mu-item-icon">❌</div>
              ) : (
                <button className="mu-cover" onClick={() => triggerCover(q.id)} disabled={q.status !== 'queued'} title="Set / replace cover art">
                  {q.coverUrl ? <img src={q.coverUrl} alt="cover" /> : <FiImage />}
                  {q.status === 'saving' ? <span className="mu-cover-state">⏳</span> : q.status === 'done' ? <span className="mu-cover-state">✅</span> : null}
                </button>
              )}
              <div className="mu-item-fields">
                <input className="mu-field mu-field-title" value={q.title} placeholder="Title" onChange={(e) => updateField(q.id, 'title', e.target.value)} disabled={q.status !== 'queued'} />
                <input className="mu-field" value={q.artist} placeholder="Artist" onChange={(e) => updateField(q.id, 'artist', e.target.value)} disabled={q.status !== 'queued'} />
                <input className="mu-field" value={q.album} placeholder="Album (optional)" onChange={(e) => updateField(q.id, 'album', e.target.value)} disabled={q.status !== 'queued'} />
                <div className="mu-item-meta">
                  {fmtMb(q.file.size)}
                  {q.durationSeconds ? ` · ${Math.floor(q.durationSeconds / 60)}:${String(Math.floor(q.durationSeconds % 60)).padStart(2, '0')}` : ''}
                  {q.isVideo ? ' · video' : ''}
                  {q.errorMsg && <span className="mu-item-error"> — {q.errorMsg}</span>}
                </div>
              </div>
              {q.status === 'queued' && <button className="mu-item-remove" onClick={() => removeItem(q.id)} title="Remove"><FiX /></button>}
            </div>
          ))}

          {doneCount > 0 && <p className="mu-done-note">{doneCount} track{doneCount === 1 ? '' : 's'} added to your library.</p>}
        </div>
      )}

      {showConsent && (
        <MusicLegalModal
          doc={UPLOAD_CONSENT}
          intro={`You're about to add ${queuedCount} file${queuedCount === 1 ? '' : 's'} to your library.`}
          confirmLabel="Certify & upload"
          cancelLabel="Cancel"
          busy={committing}
          defaultSignature={signatureDefault}
          onAccept={doCommit}
          onCancel={() => setShowConsent(false)}
        />
      )}
    </div>
  );
};

export default MusicUpload;
